/**
 * Nameplate + HP/MP Bar Display Config
 *
 * The overhead nameplate stack drawn above every player's head — name tag,
 * HP bar, MP bar, and the remote-caster cast bar. The SAME PlayerRenderer
 * serves both the local player and remote players, so every value here
 * applies uniformly to both.
 *
 * Three-section file (the canonical "defaults + dev override" pattern, see
 * chat-bubble.config.ts):
 *
 *   1. NAMEPLATE_DEFAULTS — immutable production defaults. Treat as the
 *      shipped look; do not bake per-environment tweaks here.
 *
 *   2. NAMEPLATE_DEV_OVERRIDE — partial override layered on top of the
 *      defaults. This is where to tweak bar size / colors / font while
 *      iterating; leave `{}` to ship production defaults.
 *
 *   3. NAMEPLATE — the merged result. Consumers import THIS (never the raw
 *      slices) so the merge happens in one canonical place.
 *
 * Colors are numeric hex (0xRRGGBB). Bars are plate bars
 * (src/game/rendering/plates/PlateBar.ts): tinted sprites with square corners
 * and a 1 px outline, so a changing value never redraws a Graphics (plan 13
 * §12 — DJ, 2026-10-07: plates stay simple; the stylised look is the HUD's).
 */

export interface NameplateBarColors {
  /** HP fill when above the `warn` fraction. */
  hpHealthy: number;
  /** HP fill once HP drops below the `warn` fraction (but above `critical`). */
  hpWarn: number;
  /** HP fill once HP drops below the `critical` fraction. */
  hpCritical: number;
  /** MP bar fill color. */
  mp: number;
}

export interface NameplateBarThresholds {
  /** HP fraction below which the bar turns `hpWarn`. */
  warn: number;
  /** HP fraction below which the bar turns `hpCritical`. */
  critical: number;
}

export interface NameplateBarStyle {
  /** Bar width in px (centered on the player). */
  width: number;
  /** Height of each bar in px. */
  height: number;
  /** Vertical gap between the HP and MP bars in px. */
  gap: number;
  /** Background track color (drawn full-width behind the fill). */
  bgColor: number;
  /** Background track alpha. */
  bgAlpha: number;
  /** Border/outline color drawn around each bar. */
  borderColor: number;
  /** Border/outline width in px. 0 disables the border. */
  borderWidth: number;
  /** Border alpha. */
  borderAlpha: number;
}

export interface NameplateNameStyle {
  fontFamily: string;
  /** Font size in px. */
  fontSize: number;
  /** Text color (hex). */
  color: number;
  /** Outline width in px. 0 disables the stroke. The stroke color is black
   * (the name-tag substrate renders a fixed black outline). */
  strokeWidth: number;
  fontWeight: 'normal' | 'bold';
  letterSpacing: number;
  textResolution: number;
  roundPixels: boolean;
}

export interface NameplateBarLabelStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  strokeWidth: number;
}

export interface NameplateCastBarStyle {
  /** Cast-bar width in px (centered on the player). */
  width: number;
  /** Cast-bar height in px. */
  height: number;
  /** Background track color. */
  bgColor: number;
  /** Background track alpha. */
  bgAlpha: number;
  /** Fill (progress) color. */
  fillColor: number;
  /** Border color. */
  borderColor: number;
  /** Border width in px. 0 disables. */
  borderWidth: number;
  /** Border alpha. */
  borderAlpha: number;
  /** Ability-name label colour (hex), drawn INSIDE the bar (plan 15 §2.5). */
  labelColor: number;
  /** Dark outline under the label, px — it must read on the brass fill AND
   *  on the dark empty track. */
  labelStrokeWidth: number;
  /** Ability-name label font size in px. */
  labelFontSize: number;
  /** Label inset from the bar's left edge, px (the label is left-aligned). */
  labelInsetX: number;
}

/**
 * The overhead stack — ONE ordered layout definition for every element
 * PlayerRenderer floats above a player's head. Rows are listed bottom → top;
 * each row's vertical position is DERIVED from the stack instead of carrying
 * its own independent magic offsetY (the old 10 / 44 / 52 / 58 constants).
 *
 * Per-row anchor semantics (what the offset points at) are unchanged from the
 * pre-stack renderer:
 *   - name:          name-tag center
 *   - bars:          TOP of the HP bar (HP + MP extend DOWNWARD from here,
 *                    into the gap the name row's `advance` reserves)
 *   - castBar:       TOP of the cast bar (ability label floats just above)
 *   - classResource: combo-pip row center (styling in classResource.config.ts;
 *                    only its POSITION lives here)
 */
export type NameplateStackRowId = 'name' | 'bars' | 'castBar' | 'classResource';

export interface NameplateStackRow {
  id: NameplateStackRowId;
  /** px the NEXT row's anchor sits above THIS row's anchor (row extent + breathing room). */
  advance: number;
}

export interface NameplateStackConfig {
  /** Distance in px from the sprite anchor (player.position.y) up to the bottom row's anchor. */
  baseOffsetY: number;
  /** Rows bottom → top. */
  rows: NameplateStackRow[];
}

/** Resolve the stack into per-row offsets (px above the sprite anchor). */
export function computeStackOffsets(
  stack: NameplateStackConfig,
): Record<NameplateStackRowId, number> {
  const offsets = {} as Record<NameplateStackRowId, number>;
  let y = stack.baseOffsetY;
  for (const row of stack.rows) {
    offsets[row.id] = y;
    y += row.advance;
  }
  return offsets;
}

/**
 * The overhead layout pass (plan 15, phase 3; src/game/rendering/overhead/):
 * every mob, player and NPC label group in view is placed by priority, and
 * lower tiers move up out of the way of higher ones, then fade their name
 * when there is still no room. It replaced the mob-only vertical-stagger
 * declutter (U2, 2026-09-28), keeping its rule that a plate never climbs
 * arbitrarily far from its own sprite (`maxOffsetY`).
 */
export interface NameplateDeclutterConfig {
  /** Master on/off. false = every label at its natural spot (no layout). */
  enabled: boolean;
  /** Air between two stacked label groups, px. */
  gapY: number;
  /** Horizontal air: groups closer than this, edge to edge, collide, px. */
  padX: number;
  /** Largest upward nudge for a mob or player group, px. */
  maxOffsetY: number;
  /** Largest nudge for an NPC name (clears the local player's stack), px. */
  npcMaxOffsetY: number;
  /** Extra clearance before a raised group drops back to its spot, px. */
  hysteresisPx: number;
  /** Re-solve when a group moved more than this since the last solve, px. */
  moveQuantumPx: number;
  /** Re-solve at least this often, ms (safety net). */
  maxIntervalMs: number;
  /** Nudges and fades ease over this long, ms. */
  easeMs: number;
  /** `smart` names: tier 4–5 names start fading this far from the player, px. */
  nameFadeStartPx: number;
  /** …and reach `nameFadeMinAlpha` this far away, px (world distance). */
  nameFadeEndPx: number;
  nameFadeMinAlpha: number;
}

/**
 * Mob nameplate fit — how the name tag and mob health bar anchor to the LIVE
 * sprite (not just the authored hitbox). U2 (2026-09-28): DJ's review found
 * mob plates reading as loose floating text that did not fit their sprites;
 * these tokens are the single tuning surface consumed by MobRenderer +
 * PixiNameTag. All values are world px unless noted.
 */
export interface NameplateMobFitConfig {
  /** Name plate BOTTOM edge sits this far above the sprite's rendered top. */
  spriteGap: number;
  /** Mob health-bar TOP edge sits this far above the sprite's rendered top. */
  barGap: number;
  /** Authority dot sits this far above the name plate's bottom anchor. */
  authorityDotOffset: number;
  /** A mob name stays on one line up to this width and wraps only past it
   *  (long player-named mobs stay bounded). Plan 15 §2.6: the old
   *  sprite-derived width (1.7 × sprite, floored at 84 px) broke two-word
   *  names on small mobs ("Gree / Slim") into a second line under the plate. */
  maxWrapWidth: number;
  /**
   * Compact-viewport readability floor: when the fitted world scale would
   * render the plate below this many SCREEN px, the tag counter-scales up.
   */
  minScreenFontPx: number;
  /** Hard cap on that counter-scale so plates never balloon. */
  maxReadableScale: number;
}

export interface NameplateConfig {
  bar: NameplateBarStyle;
  barColors: NameplateBarColors;
  barThresholds: NameplateBarThresholds;
  stack: NameplateStackConfig;
  declutter: NameplateDeclutterConfig;
  /** Mob plate/bar attachment + compact-viewport readability tokens (U2). */
  mobFit: NameplateMobFitConfig;
  /**
   * Mob health-bar variant. Mobs draw a single HP bar above the sprite (no MP
   * row), so this is a `NameplateBarStyle` sibling of `bar` — same rounded /
   * bordered aesthetic, sized a touch tighter than the player stack. Mobs reuse
   * `barColors` (hp* fields) + `barThresholds` for the health→color mapping;
   * the `mp`/`gap` fields are inert here (no second bar).
   */
  mobBar: NameplateBarStyle;
  name: NameplateNameStyle;
  barLabel: NameplateBarLabelStyle;
  castBar: NameplateCastBarStyle;
}

export const NAMEPLATE_DEFAULTS: NameplateConfig = {
  bar: {
    // Bigger + more readable than the old 60×7. Still tasteful for a
    // MapleStory-style 2D camera — wide enough to read HP at a glance.
    width: 72,
    height: 10,
    gap: 2,
    bgColor: 0x0a0e16,
    bgAlpha: 0.82,
    // Thin dark outline so bars pop against bright terrain.
    borderColor: 0x000000,
    borderWidth: 1,
    borderAlpha: 0.9,
  },
  barColors: {
    // HUD-restyle token-align (Lane D.1): the health→color thresholds now
    // mirror the theme's SEMANTIC palette (src/game/ui/theme/colors.ts) 1:1
    // instead of a divergent world-space set. Numeric (0xRRGGBB) twins of the
    // theme string tokens — this shared config can't import the game/ui theme
    // module (would cycle shared→game→shared), so the values are mirrored by
    // hand with the token name that owns each. Mob bars keep this threshold
    // system (style guide §5 — thresholds stay; only the hues token-align).
    hpHealthy: 0x42d77d, // COLORS.success  (RAW.netGreen #42d77d)
    hpWarn: 0xffd23f, // COLORS.warning  (RAW.amber   #ffd23f)
    hpCritical: 0xff4757, // COLORS.danger   (RAW.scarlet #ff4757)
    mp: 0x3498db, // COLORS.mana     (RAW.blue    #3498db)
  },
  barThresholds: {
    warn: 0.5,
    critical: 0.25,
  },
  stack: {
    // Bottom row (name) anchors 10px above the sprite anchor — just over the
    // head of the 40×40 physics box. Advances reproduce the pre-stack layout
    // closely: name@10 → bars@46 (HP top; the 36px advance reserves room
    // for the 22px HP/MP stack that draws DOWNWARD from the bars anchor)
    // → castBar@60 (12 px, label inside — plan 15 §2.5) → classResource@66.
    baseOffsetY: 10,
    rows: [
      { id: 'name', advance: 36 },
      { id: 'bars', advance: 14 },
      { id: 'castBar', advance: 6 },
      { id: 'classResource', advance: 0 },
    ],
  },
  declutter: {
    enabled: true,
    gapY: 2,
    padX: 4,
    // At most ~one mob group height off its own sprite; past that the name
    // yields (fades by tier) instead of floating away.
    maxOffsetY: 40,
    // An NPC caption clears the local player's whole stack (the old fixed
    // 64 px lift, worldLabelLayout.npcNameOffset, now a layout nudge).
    npcMaxOffsetY: 72,
    hysteresisPx: 10,
    moveQuantumPx: 4,
    maxIntervalMs: 500,
    easeMs: 120,
    nameFadeStartPx: 450,
    nameFadeEndPx: 900,
    nameFadeMinAlpha: 0.25,
  },
  mobFit: {
    // Plate bottom 16px over the sprite's rendered top; bar top 12px over it
    // (8px tall), so the bar nests between the plate and the head with a 4px
    // plate-to-bar gap. These replace the old hardcoded -25 / -12 constants.
    spriteGap: 16,
    barGap: 12,
    // 15px name + ~9px air above the text box's bottom anchor.
    authorityDotOffset: 24,
    // One line up to 180px; only longer (player-style) names wrap.
    maxWrapWidth: 180,
    // On a 390px-wide viewport the fit-to-letterbox world scale is ~0.30 and a
    // 15px plate would render at ~4.6px. The tag counter-scales up to keep a
    // 10px screen floor, hard-capped at 2.5x so plates never balloon.
    minScreenFontPx: 10,
    maxReadableScale: 2.5,
  },
  mobBar: {
    // Tighter than the player bar (old mobs were 5px tall, width ≈ mob.width
    // * 0.8). 8px tall reads cleanly without crowding the sprite; the renderer
    // still derives a per-mob width from the sprite (clamped to >= this.width)
    // so wide mobs get a proportional bar — `width` here is the floor/default.
    width: 54,
    height: 8,
    gap: 0,
    bgColor: 0x0a0e16,
    bgAlpha: 0.82,
    borderColor: 0x000000,
    borderWidth: 1,
    borderAlpha: 0.9,
  },
  name: {
    fontFamily: '\'Inter\', \'Segoe UI\', system-ui, sans-serif',
    // Warm parchment text tone from the ink&brass mock (--text #e8e2d4) —
    // token-align (Lane D.1) off the old cold off-white; black stroke stays
    // for legibility on bright terrain (style guide §7, no legibility trade).
    fontSize: 15,
    color: 0xe8e2d4, // mock --text (ink&brass parchment)
    strokeWidth: 3,
    fontWeight: 'bold',
    letterSpacing: 0.15,
    textResolution: 2,
    roundPixels: true,
  },
  barLabel: {
    fontFamily: '\'Inter\', \'Segoe UI\', system-ui, sans-serif',
    fontSize: 9,
    fontWeight: 'bold',
    strokeWidth: 2,
  },
  castBar: {
    // CASTING-FEEL #3a punch-up (2026-07-09): the old 56×5 sliver read as a
    // second MP bar; 72 wide matches the player bars. Plan 15 §2.5
    // (2026-10-08): 12 px tall with the ability name INSIDE it, which removes
    // the label row that floated above every caster's stack. It stays 2 px
    // clear of the bars row below (bars row advance is 14).
    width: 72,
    height: 12,
    bgColor: 0x0a0e16,
    bgAlpha: 0.88,
    // Brass fitting tone (ACCENT.brassBright #c4a469) — token-align (Lane D.1)
    // off the old raw gold so the cast bar reads as a warm brass fitting.
    fillColor: 0xc4a469, // ACCENT.brassBright
    borderColor: 0x000000,
    borderWidth: 1,
    borderAlpha: 0.9,
    // Parchment with a dark outline: plan 15 asked for dark ink, but dark ink
    // vanished over the empty (dark) part of the track early in a cast; an
    // outlined light label reads on both the brass fill and the track.
    labelColor: 0xf4ecd8,
    labelStrokeWidth: 2,
    labelFontSize: 9,
    labelInsetX: 3,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through to
 * `NAMEPLATE_DEFAULTS`. Add temporary tuning here while iterating; promote to
 * defaults once happy.
 *
 * Examples:
 *   bar: { ...NAMEPLATE_DEFAULTS.bar, width: 96 }   // wider bars for a screenshot
 *   barColors: { ...NAMEPLATE_DEFAULTS.barColors, mp: 0xff00ff }  // hot MP for QA
 */
export const NAMEPLATE_DEV_OVERRIDE: Partial<NameplateConfig> = {};

export const NAMEPLATE: NameplateConfig = {
  ...NAMEPLATE_DEFAULTS,
  ...NAMEPLATE_DEV_OVERRIDE,
};

/**
 * Resolved per-row overhead offsets (px above the sprite anchor), derived
 * from the merged stack. Renderers consume THIS — never a hand-tuned
 * offsetY — so reordering / spacing the stack is a one-place config change.
 */
export const NAMEPLATE_STACK_OFFSETS = computeStackOffsets(NAMEPLATE.stack);

/**
 * Bounded readable counter-scale for a world-space label (U2, 2026-09-28).
 *
 * The Pixi world container is fit-to-letterbox scaled (see
 * computeWorldTransform): on a 390px-wide viewport a 15px world-space plate
 * renders at ~4.6 screen px. This pure function returns the multiplier that
 * lifts the fitted font to `minScreenFontPx`, clamped to
 * [1, maxReadableScale] so a plate can grow but never balloon.
 *
 * `fitScale` is the world-container scale (canvas px per world unit); pass 1
 * when unknown (identity). Invalid inputs return 1 (never NaN/Infinity).
 */
export function computeReadableWorldScale(
  fitScale: number,
  fontSizePx: number,
  cfg: Pick<NameplateMobFitConfig, 'minScreenFontPx' | 'maxReadableScale'>,
): number {
  if (!Number.isFinite(fitScale) || fitScale <= 0) return 1;
  if (!Number.isFinite(fontSizePx) || fontSizePx <= 0) return 1;
  const cap = Number.isFinite(cfg.maxReadableScale) && cfg.maxReadableScale >= 1
    ? cfg.maxReadableScale
    : 1;
  const floor = Number.isFinite(cfg.minScreenFontPx) && cfg.minScreenFontPx > 0
    ? cfg.minScreenFontPx
    : 0;
  const screenFontPx = fontSizePx * fitScale;
  if (floor <= 0 || screenFontPx >= floor) return 1;
  const required = floor / screenFontPx;
  return Math.min(cap, Math.max(1, required));
}

